import type { GridItemProps } from "@chakra-ui/react";
import { Skeleton, Stat, StatLabel, StatNumber } from "@chakra-ui/react";
import PermissionField from "components/common/permission-field";
import ProfileCard from "components/profile/profile-card";
import { abbreviateCurrency } from "utils/number";
import type { CompanyProfile } from "types/api/company/details/overview";

interface SummaryCardProps extends GridItemProps {
  isLoaded?: boolean;
  label: string;
  borderColor?: string;
}

const SummaryCard = ({
  children,
  isLoaded = false,
  label,
  borderColor = "blue.500",
  ...props
}: SummaryCardProps) => (
  <ProfileCard
    colSpan={{ base: 6, lg: 3 }}
    borderLeftWidth={4}
    borderColor={borderColor}
    {...props}
  >
    <Stat>
      <StatLabel fontSize={["xs", "sm"]}>{label}</StatLabel>
      <StatNumber fontSize={["xl", "2xl"]} lineHeight={1} mt={2}>
        {isLoaded ? children : <Skeleton h="24px" w="50%" />}
      </StatNumber>
    </Stat>
  </ProfileCard>
);

interface SummaryCardRowProps {
  isLoaded: boolean;
  companyProfile: CompanyProfile;
}

const SummaryCardRow = ({ isLoaded, companyProfile }: SummaryCardRowProps) => {
  const getTotalFunding = () => {
    const totalFunding = companyProfile?.companyProfileIndex?.indexTotalFunding;

    return totalFunding ? abbreviateCurrency(totalFunding) : "--";
  };

  const getLatestValuation = () => {
    const latestValuation =
      companyProfile?.companyProfileIndex?.indexLatestValuation;

    return latestValuation ? abbreviateCurrency(latestValuation) : "--";
  };

  return (
    <>
      <SummaryCard
        label="Year Founded"
        borderColor="blue.500"
        isLoaded={isLoaded}
        data-cy="year-founded-card"
      >
        {companyProfile?.yearFounded || "--"}
      </SummaryCard>

      <SummaryCard
        label="Funding Rounds"
        borderColor="yellow.500"
        isLoaded={isLoaded}
        data-cy="funding-rounds-card"
      >
        {companyProfile?.totalCompanyRounds || "--"}
      </SummaryCard>

      <SummaryCard
        label="Total Funding"
        borderColor="orange.500"
        isLoaded={isLoaded}
        data-cy="total-funding-card"
      >
        <PermissionField
          permission="companyOverviewFunding"
          alt="$0.00 BN"
          blurAmount={12}
        >
          {getTotalFunding()}
        </PermissionField>
      </SummaryCard>

      <SummaryCard
        label={`Latest Valuation${
          companyProfile?.companyProfileIndex?.indexLatestValuationYear
            ? ` (${companyProfile?.companyProfileIndex?.indexLatestValuationYear})`
            : ""
        }`}
        borderColor="rose.500"
        isLoaded={isLoaded}
        data-cy="latest-valuation-card"
      >
        <PermissionField
          permission="companyOverviewValuation"
          alt="$0.00 BN"
          blurAmount={12}
        >
          {getLatestValuation()}
        </PermissionField>
      </SummaryCard>
    </>
  );
};

export default SummaryCardRow;
